import {flatMap} from 'lodash';

import {Comparator} from '../../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {WritableResourceMap} from '../../../../../libs/shared/core/src/store/commonAppTypes';
import {compareName} from '../../../../../srcCommon/helpers/localeHelpers';
import {AnswerOrFolderModel, RAnswerFolder} from '../../../../models/api/answerFolderModel';

export const defaultAnswerOrFolderComparator: Comparator<AnswerOrFolderModel> = (a, b) => {
  if (RAnswerFolder.is(a) === RAnswerFolder.is(b)) {
    return compareName(a.name, b.name);
  }

  return RAnswerFolder.is(a) ? -1 : 1;
};

// If you consider the entire folder hierarchy in an expanded form, where each level is sorted
// alphabetically, this will return an array representing all of those folders from top to bottom.
//
// e.g. For a folder hierarchy like this:
//     FolderA
//       |- AChild_ABC
//       |   |- AGrandchild
//       |- AChild_XYZ
//     FolderB
//       |- BChild
//
// This will return: [FolderA, AChild_ABC, AGrandchild, AChild_XYZ, FolderB, BChild]
export function hierarchicalFolderSort<R extends AnswerOrFolderModel>(
  items: ReadonlyArray<R>,
  itemComparator = defaultAnswerOrFolderComparator,
): ReadonlyArray<R> {
  const folderChildMap = buildFolderChildMap(items);
  const topLevelItems = items.filter((item) =>
    RAnswerFolder.is(item) ? !item.parentFolderId : !item.folderId,
  );

  return hierarchicalSortStep(topLevelItems, folderChildMap, itemComparator);
}

// This will do the following given a list of folders:
// 1. Sort the given folders.
// 2. Recursively hierarchically sort each folder's subtree into a flat list.
// 3. Concatenate all the flat lists.
function hierarchicalSortStep<R extends AnswerOrFolderModel>(
  items: Array<R>,
  folderChildMap: WritableResourceMap<Array<R>>,
  itemComparator: Comparator<R>,
): Array<R> {
  if (items.length === 0) {
    return [];
  }

  const sortedItems = items.sort(itemComparator);

  return flatMap(sortedItems, (item) => {
    const childItems = folderChildMap[item.id] || [];
    return [item, ...hierarchicalSortStep(childItems, folderChildMap, itemComparator)];
  });
}

// This goes through each of the folders, which has a 'parentFolderId` on it, and builds a map of:
//    {
//      [folderA.id]: [folderAChild1, folderAChild2, ...]
//      [folderB.id]: [folderBChild1, folderBChild2, ...]
//      ...
//    }
//
/* eslint-disable no-param-reassign */
function buildFolderChildMap<R extends AnswerOrFolderModel>(
  items: ReadonlyArray<R>,
): WritableResourceMap<Array<R>> {
  return items.reduce((folderChildMap: any, item) => {
    // Initialize folder's children to [] if necessary.
    if (!(item.id in folderChildMap) && RAnswerFolder.is(item)) {
      folderChildMap[item.id] = [];
    }

    const parentId = RAnswerFolder.is(item) ? item.parentFolderId : item.folderId;

    // If this folder has no parent we can bail out.
    if (!parentId) {
      return folderChildMap;
    }

    // Otherwise add this folder to its parents list of children.
    if (!(parentId in folderChildMap)) {
      folderChildMap[parentId] = [item];
    } else {
      folderChildMap[parentId].push(item);
    }

    return folderChildMap;
  }, {});
}
/* eslint-enable no-param-reassign */
